import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import Icon from "../AppIcon";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import { useLanguage } from "../../context/LanguageContext";

const ROUTE_MAP: Record<string, string> = {
  home: "/",
  about: "/about",
  academy: "/academy",
  trade: "/trade",
  vault: "/vault",
  testimonials: "/testimonials",
  help: "/help",
  contact: "/contact",
  privacy: "/privacy",
  terms: "/terms",
  login: "/login",
  register: "/register",
  dashboard: "/dashboard",
  admin: "/admin",
};

const PATH_TO_PAGE: Record<string, string> = {
  "/": "home",
  "/about": "about",
  "/academy": "academy",
  "/trade": "trade",
  "/vault": "vault",
  "/testimonials": "testimonials",
};

const NAV_IDS = ["home", "trade", "academy", "vault", "about"] as const;

const Navbar: React.FC = () => {
  const { t } = useLanguage();
  const navItems = NAV_IDS.map(id => ({ id, label: t(`nav.${id}`) }));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const activePage = PATH_TO_PAGE[location.pathname] || "";

  const userName = user?.name || user?.email?.split("@")[0] || "User";
  const userEmail = user?.email || "";
  const avatarSeed = encodeURIComponent(user?.name || user?.email || "User");
  const avatarUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${avatarSeed}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`;

  const handleNavigate = (page: string) => {
    const path = ROUTE_MAP[page] || `/${page}`;
    navigate(path);
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    await logout();
    navigate("/");
  };

  // Hide/show navbar on scroll
  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    setScrolled(currentY > 10);
    if (currentY > lastScrollY.current && currentY > 80) {
      setVisible(false); // scrolling down
    } else {
      setVisible(true); // scrolling up
    }
    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  return (
    <>
      <nav
        className={`
          fixed top-0 inset-x-0 z-50 h-16
          transition-all duration-300 ease-out
          ${visible ? "translate-y-0" : "-translate-y-full"}
          ${scrolled ? "bg-background/90 backdrop-blur-xl shadow-lg shadow-black/5 border-b border-primary/5" : "bg-transparent"}
        `}
      >
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => handleNavigate("home")}
            className="hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
          >
            <img src="/logo.png" alt="Acebarter" width="115" height="32" className="h-8" />
          </button>

          {/* Desktop Navigation — centered */}
          <div className="hidden md:flex items-center gap-0.5 bg-card/40 backdrop-blur-sm rounded-full px-1.5 py-1 border border-primary/5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`
                  relative px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-200
                  ${activePage === item.id
                    ? "bg-accent text-accent-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                  }
                `}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />

            <button
              onClick={toggleTheme}
              aria-label="Toggle theme" className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-card/60 transition-colors"
              title={isDark ? "Light Mode" : "Dark Mode"}
            >
              <Icon name={isDark ? "Sun" : "Moon"} size={17} />
            </button>

            {isAuthenticated ? (
              <>
                <button
                  onClick={() => handleNavigate("dashboard")}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground rounded-full transition-colors shadow-sm"
                >
                  <Icon name="LayoutDashboard" size={15} />
                  Dashboard
                </button>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="w-9 h-9 rounded-full ring-2 ring-primary/10 hover:ring-accent/30 transition-all overflow-hidden"
                  >
                    <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-3 w-60 bg-card border border-primary/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 z-50">
                      <div className="px-4 py-3.5 border-b border-primary/5 bg-muted/20">
                        <div className="flex items-center gap-3">
                          <img
                            src={avatarUrl}
                            alt={userName}
                            className="w-10 h-10 rounded-full ring-2 ring-accent/20 bg-card object-cover shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{userName}</p>
                            <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        {[
                          { label: "Dashboard", icon: "LayoutDashboard", path: "/dashboard" },
                          { label: "Trade", icon: "TrendingUp", path: "/dashboard?section=live-trading" },
                          { label: "Wallet", icon: "Wallet", path: "/dashboard?section=deposit" },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary/5 transition-colors"
                          >
                            <Icon name={item.icon} size={16} className="text-muted-foreground" />
                            {item.label}
                          </button>
                        ))}
                        {isAdmin && (
                          <button
                            onClick={() => { navigate("/admin"); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-accent hover:bg-primary/5 transition-colors"
                          >
                            <Icon name="ShieldCheck" size={16} />
                            Admin Panel
                          </button>
                        )}
                      </div>
                      <div className="border-t border-primary/5 py-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/5 transition-colors"
                        >
                          <Icon name="LogOut" size={16} />
                          Log Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleNavigate("login")}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => handleNavigate("register")}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground rounded-full transition-colors shadow-sm"
                >
                  Get Started
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-xl text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Open menu"
          >
            <Icon name={mobileMenuOpen ? "X" : "Menu"} size={22} />
          </button>
        </div>
      </nav>

      {/* Spacer to prevent content from going under fixed nav */}
      <div className="h-16" />

      {/* Mobile Menu — full-screen overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Panel */}
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-card shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="h-16 flex items-center justify-between px-5 border-b border-primary/5 flex-shrink-0">
              <img src="/logo.png" alt="Acebarter" width="100" height="28" className="h-7" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-xl text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* User banner (logged in) */}
            {isAuthenticated && (
              <div className="px-5 py-4 border-b border-primary/5 bg-muted/10">
                <div className="flex items-center gap-3">
                  <img
                    src={avatarUrl}
                    alt={userName}
                    className="w-11 h-11 rounded-full ring-2 ring-accent/20 bg-card object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{userName}</p>
                    <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Nav items */}
            <div className="flex-1 overflow-y-auto py-3 px-3">
              <div className="space-y-0.5">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors
                      ${activePage === item.id
                        ? "bg-accent/10 text-accent"
                        : "text-foreground hover:bg-primary/5"
                      }
                    `}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="my-4 border-t border-primary/5" />

              {/* Utilities */}
              <div className="space-y-0.5">
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium text-foreground hover:bg-primary/5 transition-colors"
                >
                  <Icon name={isDark ? "Sun" : "Moon"} size={18} className="text-muted-foreground" />
                  {isDark ? "Light Mode" : "Dark Mode"}
                </button>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="p-4 border-t border-primary/5 flex-shrink-0 space-y-2">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => handleNavigate("dashboard")}
                    className="w-full py-3 rounded-xl text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground transition-colors"
                  >
                    Go to Dashboard
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleNavigate("admin")}
                      className="w-full py-3 rounded-xl text-sm font-medium text-accent bg-accent/10 hover:bg-accent/15 transition-colors"
                    >
                      Admin Panel
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/5 transition-colors"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleNavigate("register")}
                    className="w-full py-3 rounded-xl text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground transition-colors"
                  >
                    Get Started
                  </button>
                  <button
                    onClick={() => handleNavigate("login")}
                    className="w-full py-3 rounded-xl text-sm font-medium text-foreground bg-card-foreground/5 hover:bg-card-foreground/10 transition-colors"
                  >
                    Log In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
